import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getAllReports, downloadReport } from "../services/reportService";
import { Report } from "../types/Report";
import { AuthContext } from "../context/AuthContext";
import Button from "../components/ui/Button";
import LanguageSwitcher from "../components/ui/LanguageSwitcher";
import { getTranslatedError } from "../utils/translateError";

const ReportsOverviewPage: React.FC = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const authContext = useContext(AuthContext);
	const [reports, setReports] = useState<Report[]>([]);
	const [loading, setLoading] = useState<boolean>(true);
	const [error, setError] = useState<string | null>(null);
	const [downloadingIds, setDownloadingIds] = useState<Set<string>>(new Set());

	if (!authContext) {
		throw new Error("ReportsOverviewPage must be used within AuthProvider");
	}

	const { logout } = authContext;

	const handleLogout = async () => {
		try {
			await logout();
			navigate("/login");
		} catch (error) {
			console.error("Logout failed:", error);
		}
	};

	useEffect(() => {
		fetchReports();
	}, []);

	const fetchReports = async () => {
		try {
			setLoading(true);
			setError(null);
			const data = await getAllReports();
			setReports(data);
		} catch (err) {
			setError(getTranslatedError(err, "errors.loadReportsFailed"));
		} finally {
			setLoading(false);
		}
	};

	const handleDownload = async (reportId: string) => {
		try {
			setDownloadingIds((prev) => new Set(prev).add(reportId));
			await downloadReport(reportId);
		} catch (err) {
			alert(getTranslatedError(err, "errors.downloadReportFailed"));
		} finally {
			setDownloadingIds((prev) => {
				const newSet = new Set(prev);
				newSet.delete(reportId);
				return newSet;
			});
		}
	};

	if (loading) {
		return (
			<div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-gray-100 via-gray-50 to-primary-50">
				<div className="text-center">
					<div className="inline-block animate-spin rounded-full h-10 w-10 border-4 border-primary-200 border-t-primary-400 mb-4"></div>
					<div className="text-gray-600 font-medium">{t("reports.loading")}</div>
				</div>
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-gradient-to-br from-gray-100 via-gray-50 to-primary-50">
			<header className="bg-white/90 backdrop-blur-sm shadow-soft border-b border-gray-200/50 sticky top-0 z-10">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
					<div className="flex flex-col sm:flex-row justify-between items-start sm:items-center py-5 gap-4">
						<h1 className="text-3xl font-bold text-gradient">
							{t("reports.title")}
						</h1>
						<div className="flex items-center gap-2 sm:gap-4 w-full sm:w-auto">
							<LanguageSwitcher />
							<Button
								type="button"
								variant="primary"
								size="sm"
								onClick={() => navigate("/report")}
								fullWidth={false}
								className="flex-1 sm:flex-none"
							>
								{t("reports.createReport")}
							</Button>
							<Button
								type="button"
								variant="secondary"
								size="sm"
								onClick={fetchReports}
								disabled={loading}
								fullWidth={false}
								className="flex-1 sm:flex-none"
							>
								{t("reports.refresh")}
							</Button>
							<Button
								type="button"
								variant="secondary"
								size="sm"
								onClick={handleLogout}
								fullWidth={false}
								className="flex-1 sm:flex-none"
							>
								{t("reports.logout")}
							</Button>
						</div>
					</div>
				</div>
			</header>

			<main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
				<div className="bg-white shadow-soft-lg rounded-2xl p-6 md:p-8 border border-gray-100">
					{error && (
						<div className="mb-4 p-4 bg-gradient-to-r from-red-50 to-red-100 border-l-4 border-red-500 text-red-700 rounded-lg shadow-sm">
							<div className="flex items-center">
								<svg
									className="w-5 h-5 mr-2 flex-shrink-0"
									fill="currentColor"
									viewBox="0 0 20 20"
								>
									<path
										fillRule="evenodd"
										d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
										clipRule="evenodd"
									/>
								</svg>
								<span className="font-semibold">{error}</span>
							</div>
						</div>
					)}

					{reports.length === 0 ? (
						<div className="text-center py-16">
							<div className="mx-auto h-16 w-16 bg-gradient-to-br from-primary-100 to-primary-200 rounded-full flex items-center justify-center mb-4">
								<svg
									className="h-8 w-8 text-primary-500"
									fill="none"
									viewBox="0 0 24 24"
									stroke="currentColor"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
									/>
								</svg>
							</div>
							<p className="text-gray-700 text-lg font-semibold mb-2">
								{t("reports.noReports")}
							</p>
							<p className="text-gray-500 text-sm">{t("reports.noReportsHint")}</p>
						</div>
					) : (
						<div className="space-y-3">
							{reports.map((report) => (
								<div
									key={report.report_id}
									className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-5 border border-gray-200 rounded-xl hover:bg-gradient-to-r hover:from-primary-50/50 hover:to-primary-100/50 transition-all duration-200 gap-4 shadow-sm hover:shadow-md"
								>
									<div className="flex-1 min-w-0">
										<h3 className="text-lg font-semibold text-gray-800 truncate mb-1">
											{report.report_name}
										</h3>
										<p className="text-sm text-gray-500">
											{report.client_name} &mdash; {report.campaign_name}
										</p>
										<p className="text-xs text-gray-400">
											{new Date(report.created_at).toLocaleDateString()}
										</p>
									</div>
									<button
										onClick={() => handleDownload(report.report_id)}
										disabled={downloadingIds.has(report.report_id)}
										className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-semibold py-2.5 px-5 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-[1.02] active:scale-[0.98] w-full sm:w-auto"
									>
										{downloadingIds.has(report.report_id) ? (
											<span className="flex items-center">
												<svg
													className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
													xmlns="http://www.w3.org/2000/svg"
													fill="none"
													viewBox="0 0 24 24"
												>
													<circle
														className="opacity-25"
														cx="12"
														cy="12"
														r="10"
														stroke="currentColor"
														strokeWidth="4"
													></circle>
													<path
														className="opacity-75"
														fill="currentColor"
														d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
													></path>
												</svg>
												{t("reports.downloading")}
											</span>
										) : (
											t("reports.download")
										)}
									</button>
								</div>
							))}
						</div>
					)}
				</div>
			</main>
		</div>
	);
};

export default ReportsOverviewPage;
